import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const CURRENCY_CONFIG = {
  USD: { symbol: '$', position: 'before', decimals: 2, thousands: ',', decimal: '.' },
  EUR: { symbol: '€', position: 'after', decimals: 2, thousands: '.', decimal: ',' },
  GBP: { symbol: '£', position: 'before', decimals: 2, thousands: ',', decimal: '.' },
  CAD: { symbol: 'C$', position: 'before', decimals: 2, thousands: ',', decimal: '.' },
  AUD: { symbol: 'A$', position: 'before', decimals: 2, thousands: ',', decimal: '.' },
  MXN: { symbol: 'MX$', position: 'before', decimals: 2, thousands: ',', decimal: '.' },
  JPY: { symbol: '¥', position: 'before', decimals: 0, thousands: ',', decimal: '.' },
  KRW: { symbol: '₩', position: 'before', decimals: 0, thousands: ',', decimal: '.' },
  CNY: { symbol: '¥', position: 'before', decimals: 2, thousands: ',', decimal: '.' },
  INR: { symbol: '₹', position: 'before', decimals: 2, thousands: ',', decimal: '.' },
  BRL: { symbol: 'R$', position: 'before', decimals: 2, thousands: '.', decimal: ',' },
  SGD: { symbol: 'S$', position: 'before', decimals: 2, thousands: ',', decimal: '.' },
  AED: { symbol: 'د.إ', position: 'after', decimals: 2, thousands: ',', decimal: '.' },
  CHF: { symbol: 'CHF', position: 'before', decimals: 2, thousands: "'", decimal: '.' },
};

const FranchiseContext = createContext(null);

export function FranchiseProvider({ children }) {
  const { api } = useAuth();
  const [currentFranchise, setCurrentFranchise] = useState(null);
  const [pricing, setPricing] = useState({
    currency: 'USD',
    symbol: '$',
    multiplier: 1,
    taxRate: 0,
    taxLabel: 'Tax',
    taxIncluded: false,
  });
  const [loading, setLoading] = useState(true);

  // Detect franchise from URL or localStorage
  useEffect(() => {
    const detectFranchise = async () => {
      try {
        // Check URL for franchise code
        const urlParams = new URLSearchParams(window.location.search);
        const franchiseCode = urlParams.get('franchise') || localStorage.getItem('selectedFranchise');
        
        if (franchiseCode) {
          const response = await api.get(`/franchises/?search=${franchiseCode}`);
          const franchises = response.data?.results || response.data || [];
          
          if (franchises.length > 0) {
            const franchise = franchises[0];
            setCurrentFranchise(franchise);
            
            if (franchise.pricing) {
              setPricing({
                currency: franchise.pricing.currency || 'USD',
                symbol: franchise.pricing.currency_symbol || '$',
                multiplier: parseFloat(franchise.pricing.price_multiplier) || 1,
                taxRate: parseFloat(franchise.pricing.tax_rate) || 0,
                taxLabel: franchise.pricing.tax_label || 'Tax',
                taxIncluded: franchise.pricing.tax_included_in_price || false,
              });
            }
            
            localStorage.setItem('selectedFranchise', franchise.code);
          }
        }
      } catch (error) {
        console.error('Failed to detect franchise:', error);
      } finally {
        setLoading(false);
      }
    };

    detectFranchise();
  }, []);

  const selectFranchise = async (franchiseId) => {
    if (!franchiseId) {
      setCurrentFranchise(null);
      setPricing({
        currency: 'USD',
        symbol: '$',
        multiplier: 1,
        taxRate: 0,
        taxLabel: 'Tax',
        taxIncluded: false,
      });
      localStorage.removeItem('selectedFranchise');
      return;
    }

    try {
      const response = await api.get(`/franchises/${franchiseId}/`);
      const franchise = response.data;
      setCurrentFranchise(franchise);
      
      if (franchise.pricing) {
        setPricing({
          currency: franchise.pricing.currency || 'USD',
          symbol: franchise.pricing.currency_symbol || '$',
          multiplier: parseFloat(franchise.pricing.price_multiplier) || 1,
          taxRate: parseFloat(franchise.pricing.tax_rate) || 0,
          taxLabel: franchise.pricing.tax_label || 'Tax',
          taxIncluded: franchise.pricing.tax_included_in_price || false,
        });
      }
      
      localStorage.setItem('selectedFranchise', franchise.code);
    } catch (error) {
      console.error('Failed to select franchise:', error);
    }
  };

  // Format price according to franchise settings
  const formatPrice = (usdAmount, options = {}) => {
    const { showCurrency = false, includesTax = false } = options;
    const amount = parseFloat(usdAmount) || 0;
    
    // Apply multiplier
    let localAmount = amount * pricing.multiplier;
    
    // Add tax if not included and requested
    if (!pricing.taxIncluded && !includesTax && pricing.taxRate > 0) {
      // Don't add tax by default - let checkout handle it
    }
    
    const config = CURRENCY_CONFIG[pricing.currency] || CURRENCY_CONFIG.USD;
    
    // Format the number
    const parts = localAmount.toFixed(config.decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, config.thousands);
    const formattedNumber = parts.join(config.decimal);
    
    // Add symbol
    let result;
    if (config.position === 'before') {
      result = `${config.symbol}${formattedNumber}`;
    } else {
      result = `${formattedNumber} ${config.symbol}`;
    }
    
    if (showCurrency && pricing.currency !== 'USD') {
      result += ` ${pricing.currency}`;
    }
    
    return result;
  };

  // Calculate local price from USD
  const convertPrice = (usdAmount) => {
    return (parseFloat(usdAmount) || 0) * pricing.multiplier;
  };

  // Calculate tax amount
  const calculateTax = (localAmount) => {
    if (pricing.taxIncluded) return 0;
    return (parseFloat(localAmount) || 0) * (pricing.taxRate / 100);
  };

  // Format price with tax breakdown
  const formatPriceWithTax = (usdAmount) => {
    const localAmount = convertPrice(usdAmount);
    const tax = calculateTax(localAmount);
    const total = localAmount + tax;
    
    return {
      subtotal: formatPrice(usdAmount),
      subtotalAmount: localAmount,
      tax: formatPrice(tax / pricing.multiplier),
      taxAmount: tax,
      taxLabel: pricing.taxLabel,
      taxRate: pricing.taxRate,
      total: formatPrice(total / pricing.multiplier),
      totalAmount: total,
      taxIncluded: pricing.taxIncluded,
    };
  };

  return (
    <FranchiseContext.Provider
      value={{
        currentFranchise,
        pricing,
        loading,
        selectFranchise,
        formatPrice,
        convertPrice,
        calculateTax,
        formatPriceWithTax,
        currency: pricing.currency,
        currencySymbol: pricing.symbol,
      }}
    >
      {children}
    </FranchiseContext.Provider>
  );
}

export function useFranchise() {
  const context = useContext(FranchiseContext);
  if (!context) {
    throw new Error('useFranchise must be used within a FranchiseProvider');
  }
  return context;
}

export default FranchiseContext;
